import { supabase } from './supabase';
import { AIUsageService } from './aiUsageService';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// Helper function to check if we're in offline mode
const isOfflineMode = () => {
  return SUPABASE_URL === 'https://offline.supabase.co' || !navigator.onLine;
};

// Helper function to create a timeout promise (still needed for generateImage)
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Request timeout')), timeoutMs)
    )
  ]);
};

// Helper function to create a dynamic timeout with progress detection
const createDynamicTimeout = (
  initialTimeoutMs: number,
  onProgress?: (phase: string, elapsed: number) => void,
  scanType?: 'inventory' | 'meal'
): {
  controller: AbortController;
  extendTimeout: (additionalTime: number) => void;
  cleanup: () => void;
} => {
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout;
  let startTime = Date.now();
  let currentTimeout = initialTimeoutMs; // Use the provided initial timeout
  const MAX_TIMEOUT = scanType === 'meal' ? 300000 : 240000; // 5 minutes for meals, 4 minutes for inventory (increased from 3 minutes)
  const MIN_EXTENSION = 15000; // Minimum 15 seconds extension
  
  const updateTimeout = (remainingTimeMs: number) => {
    clearTimeout(timeoutId);
    const elapsed = Date.now() - startTime;
    const maxRemainingTime = MAX_TIMEOUT - elapsed;
    const actualRemainingTime = Math.min(remainingTimeMs, maxRemainingTime);
    
    console.log(`⏱️ Setting timeout: ${Math.round(actualRemainingTime / 1000)}s remaining (elapsed: ${Math.round(elapsed / 1000)}s)`);
    
    if (actualRemainingTime > 0) {
      timeoutId = setTimeout(() => {
        console.log(`❌ Timeout reached after ${Math.round((Date.now() - startTime) / 1000)}s`);
        controller.abort();
      }, actualRemainingTime);
      
      onProgress?.('timeout-updated', elapsed);
    } else {
      // Max timeout reached
      console.log(`❌ Maximum timeout (${MAX_TIMEOUT / 1000}s) reached`);
      controller.abort();
    }
  };
  
  // Initial timeout
  updateTimeout(currentTimeout);
  
  return {
    controller,
    extendTimeout: (additionalTime: number) => {
      const extension = Math.max(additionalTime, MIN_EXTENSION);
      currentTimeout += extension;
      console.log(`🔄 Extending timeout by ${Math.round(extension / 1000)}s (new total: ${Math.round(currentTimeout / 1000)}s)`);
      
      // Calculate how much time is left from the original timeout
      const elapsed = Date.now() - startTime;
      const remainingTime = currentTimeout - elapsed;
      
      updateTimeout(remainingTime);
    },
    cleanup: () => {
      console.log('🧹 Cleaning up timeout');
      clearTimeout(timeoutId);
    }
  };
};

// Helper function to measure network speed and adapt timeout
const measureNetworkSpeed = async (session: any): Promise<number> => {
  const startTime = Date.now();
  try {
    // Quick ping to Supabase to measure response time
    await fetch(`${SUPABASE_URL}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
    });
    const responseTime = Date.now() - startTime;
    
    // Categorize network speed and suggest timeout multiplier
    if (responseTime < 500) return 1.0; // Fast connection - normal timeout
    if (responseTime < 1500) return 1.5; // Medium connection - 50% longer
    if (responseTime < 3000) return 2.0; // Slow connection - double timeout
    return 2.5; // Very slow connection - 2.5x timeout
  } catch {
    return 2.0; // Unknown connection - assume slow
  }
};

/**
 * Optimizes an image file by compressing it to reduce upload time and processing cost
 * Enhanced for mobile compatibility with HEIC handling and aggressive compression
 * @param file Original image file
 * @param maxSizeKB Maximum size in KB (default: 800KB)
 * @param quality JPEG quality 0-1 (default: 0.8)
 */
export async function optimizeImage(
  file: File, 
  maxSizeKB: number = 800, 
  quality: number = 0.8,
  imageSource: 'camera' | 'library' | 'unknown' = 'unknown',
  purpose: 'ai-analysis' | 'general' | 'display' = 'general'
): Promise<File> {
  return new Promise((resolve) => {
    const fileSizeKB = file.size / 1024;
    
    // Skip compression for small images - they're already optimized!
    const compressionThreshold = purpose === 'ai-analysis' ? 500 : (purpose === 'display' ? 100 : 300); // KB
    
    if (fileSizeKB <= compressionThreshold) {
      console.log(`📷 Skipping compression - image is already small enough:`, {
        currentSize: Math.round(fileSizeKB) + 'KB',
        threshold: compressionThreshold + 'KB',
        purpose,
        fileName: file.name
      });
      resolve(file); // Return original file unchanged
      return;
    }
    
    console.log(`📷 Image compression needed:`, {
      currentSize: Math.round(fileSizeKB) + 'KB',
      threshold: compressionThreshold + 'KB',
      targetSize: maxSizeKB + 'KB',
      purpose
    });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    
    // Detect mobile PWA mode for enhanced logging
    const isStandalone = (window.navigator as any).standalone || 
                        window.matchMedia('(display-mode: standalone)').matches;
    const isMobile = window.innerWidth < 768;
    const isMobileStandalone = isMobile && isStandalone;
    
    // Purpose-aware compression settings
    const compressionSettings = purpose === 'ai-analysis' ? {
      // AI Analysis: High quality settings for ingredient detection
      camera: {
        maxDimension: 1600,  // Higher resolution for AI analysis
        targetSizeKB: Math.min(maxSizeKB, 1000), // Allow larger files for AI
        initialQuality: Math.max(quality, 0.85), // Start with high quality
        minQuality: 0.7  // Don't go below 70% quality
      },
      library: {
        maxDimension: 1400,  // Higher resolution for AI analysis
        targetSizeKB: Math.min(maxSizeKB, 800), // Allow larger files for AI
        initialQuality: Math.max(quality, 0.8), // Start with high quality
        minQuality: 0.65  // Don't go below 65% quality
      },
      unknown: {
        maxDimension: 1500,
        targetSizeKB: Math.min(maxSizeKB, 900),
        initialQuality: Math.max(quality, 0.8),
        minQuality: 0.7
      }
    } : purpose === 'display' ? {
      // Display: Optimized for display with good resolution and small file size
      camera: {
        maxDimension: 800,   // Larger than original 500 for better quality
        targetSizeKB: Math.min(maxSizeKB, 200), // Slightly larger for better quality
        initialQuality: 0.85, // Higher quality for sharp display
        minQuality: 0.7  // Don't go below 70% quality
      },
      library: {
        maxDimension: 800,   // Larger than original 500 for better quality
        targetSizeKB: Math.min(maxSizeKB, 200), // Slightly larger for better quality
        initialQuality: 0.85, // Higher quality for sharp display
        minQuality: 0.7  // Don't go below 70% quality
      },
      unknown: {
        maxDimension: 800,   // Larger than original 500 for better quality
        targetSizeKB: Math.min(maxSizeKB, 200), // Slightly larger for better quality
        initialQuality: 0.85, // Higher quality for sharp display
        minQuality: 0.7  // Don't go below 70% quality
      }
    } : {
      // General Use: Balanced compression for normal inventory/meal scanning
      camera: {
        maxDimension: 1200,
        targetSizeKB: maxSizeKB,
        initialQuality: quality,
        minQuality: 0.5
      },
      library: {
        maxDimension: 800,      // More aggressive for library images
        targetSizeKB: Math.min(maxSizeKB, 300), // Force smaller size for library
        initialQuality: Math.min(quality, 0.6), // Lower initial quality
        minQuality: 0.3
      },
      unknown: {
        maxDimension: 1000,
        targetSizeKB: Math.min(maxSizeKB, 400),
        initialQuality: Math.min(quality, 0.7),
        minQuality: 0.4
      }
    };
    
    const settings = compressionSettings[imageSource];
    
    if (isMobileStandalone) {
      console.log('📱 [PWA Debug] Starting enhanced compression:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: Math.round(file.size / 1024) + 'KB',
        imageSource,
        settings
      });
    }
    
    // Enhanced HEIC/HEIF detection and handling
    const isHEIC = file.type === 'image/heic' || 
                   file.type === 'image/heif' || 
                   file.name.toLowerCase().includes('.heic') ||
                   file.name.toLowerCase().includes('.heif');
    
    // Enhanced error handling with HEIC-specific fallback
    img.onerror = (error) => {
      console.error('❌ Failed to load image for optimization:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        isHEIC,
        imageSource,
        error: error
      });
      
      if (isMobileStandalone) {
        console.log('📱 [PWA Debug] Image load failed. Attempting fallback strategy...');
      }
      
      // HEIC-specific fallback (iOS compatibility)
      if (isHEIC) {
        console.log('📱 HEIC conversion fallback - creating JPEG file');
        const convertedFile = new File([file], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });
        resolve(convertedFile);
      } else {
        // Last resort: return original with corrected MIME type
        const correctedFile = new File([file], file.name, {
          type: 'image/jpeg',
          lastModified: file.lastModified,
        });
        resolve(correctedFile);
      }
    };
    
    img.onload = () => {
      try {
        // Calculate optimal dimensions based on source
        const maxDimension = settings.maxDimension;
        let { width, height } = img;
        
        if (isMobileStandalone) {
          console.log('📱 [PWA Debug] Image loaded successfully:', { 
            width, 
            height, 
            source: imageSource,
            maxDimension 
          });
        }
        
        console.log('📸 Original image dimensions:', { width, height });
        
        // Smart resizing algorithm (maintains aspect ratio)
        const aspectRatio = width / height;
        if (width > height && width > settings.maxDimension) {
          width = settings.maxDimension;
          height = Math.round(width / aspectRatio);
        } else if (height > settings.maxDimension) {
          height = settings.maxDimension;
          width = Math.round(height * aspectRatio);
        }
        
        console.log('📸 Optimized dimensions:', { width, height, source: imageSource });
        
        canvas.width = width;
        canvas.height = height;
        
        // Enhanced canvas quality settings (inspired by robust compression)
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Clear canvas and set white background (helps with HEIC transparency)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        
        // Draw image with high quality
        ctx.drawImage(img, 0, 0, width, height);
        
        if (isMobileStandalone) {
          console.log('📱 [PWA Debug] Image drawn to canvas, starting progressive compression');
        }
        
        // Progressive compression algorithm (inspired by robust compression)
        const progressiveCompress = async (targetQuality: number, attempt: number = 1) => {
          const maxAttempts = 6;
          
          return new Promise<void>((resolveCompress) => {
            canvas.toBlob((blob) => {
              if (!blob) {
                console.error('❌ Blob creation failed');
                resolve(file); // Fallback to original
                return;
              }
              
              const sizeKB = blob.size / 1024;
              const targetSizeKB = settings.targetSizeKB;
              
              console.log(`📸 Compression attempt ${attempt}:`, {
                quality: targetQuality.toFixed(2),
                sizeKB: Math.round(sizeKB),
                targetKB: targetSizeKB,
                source: imageSource
              });
              
              // Check if we've met our target or reached minimum quality
              if (sizeKB <= targetSizeKB || targetQuality <= settings.minQuality || attempt >= maxAttempts) {
                const optimizedFile = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                
                const reduction = Math.round(((file.size - optimizedFile.size) / file.size) * 100);
                console.log(`📷 Image optimization complete (${imageSource}):`, {
                  original: Math.round(file.size/1024) + 'KB',
                  optimized: Math.round(optimizedFile.size/1024) + 'KB',
                  reduction: reduction + '%',
                  finalQuality: targetQuality.toFixed(2)
                });
                
                if (isMobileStandalone) {
                  console.log('📱 [PWA Debug] Enhanced compression completed successfully');
                }
                
                resolve(optimizedFile);
                resolveCompress();
                return;
              }
              
              // Calculate next quality level (more aggressive for library images)
              const qualityReduction = imageSource === 'library' ? 0.15 : 0.1;
              const nextQuality = Math.max(settings.minQuality, targetQuality - qualityReduction);
              
              // Continue with next compression attempt
              setTimeout(() => {
                progressiveCompress(nextQuality, attempt + 1).then(resolveCompress);
              }, 10);
              
            }, 'image/jpeg', targetQuality);
          });
        };
        
        // Start progressive compression
        progressiveCompress(settings.initialQuality);
        
      } catch (error) {
        console.error('❌ Image processing error:', error);
        if (isMobileStandalone) {
          console.log('📱 [PWA Debug] Image processing failed, using fallback');
        }
        resolve(file); // Fallback to original
      }
    };
    
    // Enhanced file loading with timeout (iOS compatibility)
    try {
      // HEIC special handling for iOS devices
      if (isHEIC && isMobileStandalone) {
        console.log('📱 [PWA Debug] HEIC file in PWA mode - using enhanced loading');
      }
      
      const objectUrl = URL.createObjectURL(file);
      img.crossOrigin = 'anonymous';
      
             // Set up timeout for image loading (prevent hangs)
       const loadTimeout = setTimeout(() => {
         console.warn('⏰ Image load timeout - using fallback');
         if (img.onerror) {
           img.onerror(new Event('error'));
         }
       }, 15000); // 15 second timeout
      
      const originalOnLoad = img.onload;
      img.onload = (event) => {
        clearTimeout(loadTimeout);
        URL.revokeObjectURL(objectUrl);
        originalOnLoad?.call(img, event);
      };
      
      const originalOnError = img.onerror;
      img.onerror = (error) => {
        clearTimeout(loadTimeout);
        URL.revokeObjectURL(objectUrl);
        originalOnError?.call(img, error);
      };
      
      img.src = objectUrl;
      
      if (isMobileStandalone) {
        console.log('📱 [PWA Debug] Enhanced object URL created and assigned');
      }
      
    } catch (error) {
      console.error('❌ Failed to create object URL:', error);
      if (isMobileStandalone) {
        console.log('📱 [PWA Debug] Object URL creation failed - using original file');
      }
      resolve(file);
    }
  });
}

export async function recognizeImage(
  imageUrl: string, 
  scanType: 'inventory' | 'meal' = 'inventory',
  onProgressUpdate?: (phase: string, elapsed: number) => void
): Promise<any & { requiresUpgrade?: boolean; limits?: any }> {
  if (isOfflineMode()) {
    throw new Error('Image recognition is not available in offline mode');
  }

  try {
    console.log('🚀 Starting image recognition process...');
    console.log('📝 Image URL:', imageUrl.substring(0, 100) + '...');
    console.log('📝 Scan type:', scanType);
    
    // Check authentication first
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      console.error('❌ Session error:', sessionError);
      throw new Error(`Authentication error: ${sessionError.message}`);
    }
    
    if (!session?.user) {
      console.error('❌ No user in session');
      throw new Error('User not authenticated - please log in');
    }

    // Usage limits are now checked earlier in global-image-scan.ts before expensive operations
    
            // User authenticated successfully
    
    // Measure network speed to adapt timeout
    console.log('📶 Measuring network speed...');
    onProgressUpdate?.('connecting', 0);
    const networkMultiplier = await measureNetworkSpeed(session);
    console.log(`📶 Network speed multiplier: ${networkMultiplier}x`);
    
    // Prepare the payload (removed inventory data)
    const payload = {
      image: imageUrl,
      scanType
    };
    
    console.log('📦 Payload:', JSON.stringify(payload, null, 2));
    console.log('🌐 Making image recognition API call with enhanced progress tracking...');
    
    // Create dynamic timeout system
    const startTime = Date.now();
    let lastProgressTime = startTime;
    let currentPhase = 'connecting';
    
    // Calculate initial timeout based on network speed and scan type
    let baseTimeout, initialTimeout;
    
    if (scanType === 'meal') {
      // Meal scans are more complex - give them much longer timeouts
      baseTimeout = 120000; // 2 minutes base for meal scans
      initialTimeout = Math.min(baseTimeout * networkMultiplier, 300000); // Cap at 5 minutes for meals
      console.log(`🍽️ Meal scan timeout set to ${Math.round(initialTimeout / 1000)}s (generous timeout for complex analysis)`);
    } else {
      // Regular inventory scans - increased timeouts for complex receipts
      baseTimeout = 90000; // 1.5 minutes base for inventory (increased from 1 minute)
      initialTimeout = Math.min(baseTimeout * networkMultiplier, 240000); // Cap at 4 minutes for inventory (increased from 3 minutes)
      console.log(`⏱️ Inventory scan timeout set to ${Math.round(initialTimeout / 1000)}s based on network speed`);
    }
    
    const { controller, extendTimeout, cleanup } = createDynamicTimeout(initialTimeout, (phase, elapsed) => {
      console.log(`⏱️ Timeout ${phase} - Elapsed: ${Math.round(elapsed / 1000)}s`);
    }, scanType);
    
    // Enhanced progress tracking with realistic simulation
    const updateProgress = (newPhase: string) => {
      const now = Date.now();
      const phaseTime = now - lastProgressTime;
      const totalElapsed = Math.floor((now - startTime) / 1000);
      console.log(`📋 Phase '${currentPhase}' completed in ${Math.round(phaseTime / 1000)}s`);
      console.log(`🔄 Entering phase '${newPhase}'`);
      
      // Notify UI of progress first
      onProgressUpdate?.(newPhase, totalElapsed);
      
      // Smart timeout extension based on phase and timing
      if (currentPhase === 'connecting' && newPhase === 'sending-request') {
        // Just started the main request - give it plenty of time
        const extension = scanType === 'meal' ? Math.max(90000, baseTimeout) : Math.max(75000, baseTimeout); // Increased inventory extension from 60s to 75s
        console.log(`🚀 Starting main request phase, extending timeout by ${Math.round(extension / 1000)}s`);
        extendTimeout(extension);
      } else if (currentPhase === 'sending-request' && newPhase === 'processing') {
        // Request sent, edge function is now doing AI processing - this is the longest phase
        const extension = scanType === 'meal' ? Math.max(120000, baseTimeout * 2) : Math.max(120000, baseTimeout * 2); // Increased inventory extension from 90s to 120s
        console.log(`🤖 AI processing phase detected, extending timeout by ${Math.round(extension / 1000)}s`);
        extendTimeout(extension);
      } else if (currentPhase === 'processing' && newPhase === 'receiving-response') {
        // AI processing done, getting response - almost finished
        const extension = scanType === 'meal' ? 45000 : 40000; // Increased inventory extension from 30s to 40s
        console.log(`📡 AI processing complete, extending timeout by ${Math.round(extension / 1000)}s`);
        extendTimeout(extension);
      } else if (phaseTime < 10000 && newPhase !== 'validating-data') {
        // Phase completed quickly (< 10s) and not in final phase - extend moderately
        const extension = scanType === 'meal' ? Math.max(30000, phaseTime * 4) : Math.max(25000, phaseTime * 3); // Increased inventory extension from 20s to 25s
        console.log(`⚡ Quick phase completion, extending timeout by ${Math.round(extension / 1000)}s`);
        extendTimeout(extension);
      }
      
      // Update phase tracking
      currentPhase = newPhase;
      lastProgressTime = now;
    };
    
    // Simulate realistic progress during the fetch call
    const simulateProcessingProgress = () => {
      let progressInterval: NodeJS.Timeout;
      let simulationStep = 0;
      
      const progressSteps = [
        { phase: 'processing', delay: 3000 }, // Show processing after 3s
        { phase: 'processing', delay: 8000 }, // Continue processing indication
        { phase: 'processing', delay: 12000 }, // Still processing...
      ];
      
      progressInterval = setInterval(() => {
        if (simulationStep < progressSteps.length) {
          const step = progressSteps[simulationStep];
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          console.log(`🎭 Simulated progress update: ${step.phase} at ${elapsed}s`);
          onProgressUpdate?.(step.phase, elapsed);
          simulationStep++;
        }
      }, 4000); // Update every 4 seconds
      
      return () => clearInterval(progressInterval);
    };
    
    try {
      const functionUrl = `${SUPABASE_URL}/functions/v1/image-recognition`;
      
      updateProgress('sending-request');
      
      // Start progress simulation for better UX
      const stopSimulation = simulateProcessingProgress();
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      
      // Stop simulation and update with real progress
      stopSimulation();
      console.log(`🎯 Fetch completed successfully - Edge function processing complete`);
      updateProgress('receiving-response');
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      updateProgress('parsing-response');
      
      const data = await response.json();
      
      updateProgress('validating-data');
      
      cleanup();
      const endTime = Date.now();
      const totalTime = endTime - startTime;
      console.log(`⏱️ Total request took ${Math.round(totalTime / 1000)}s`);
      
      if (!data) {
        console.error('❌ No data returned from edge function');
        throw new Error('No response data received from image recognition service');
      }

      if (!data.success) {
        console.error('❌ Edge function returned error:', data.error);
        throw new Error(data.error || 'Image recognition failed');
      }

      console.log('✅ Edge function response received and validated');
      console.log('📄 Response keys:', Object.keys(data));
      console.log('📄 Response preview:', JSON.stringify(data, null, 2).substring(0, 500) + '...');
      
      // Track successful usage - use consistent feature types
      try {
        const featureType = scanType === 'meal' ? 'meal_analysis' : 'image_scan';
        await AIUsageService.incrementUsage(
          session.user.id, 
          featureType, 
          scanType === 'meal' ? 'meal_scan' : 'inventory_scan',
          'openai',
          'gpt-4-vision',
          undefined,
          totalTime,
          true
        );
      } catch (trackingError) {
        console.warn('⚠️ Usage tracking failed (continuing anyway):', trackingError);
      }
      
      // Log performance metrics for future optimization
      console.log(`📊 Performance: Network=${networkMultiplier}x, Total=${Math.round(totalTime/1000)}s`);
      
      return data;
      
    } catch (fetchError: any) {
      cleanup();
      
      const elapsed = Date.now() - startTime;
      
      // Track failed usage - use consistent feature types
      try {
        const featureType = scanType === 'meal' ? 'meal_analysis' : 'image_scan';
        await AIUsageService.incrementUsage(
          session.user.id, 
          featureType, 
          scanType === 'meal' ? 'meal_scan' : 'inventory_scan',
          'openai',
          'gpt-4-vision',
          undefined,
          elapsed,
          false,
          fetchError instanceof Error ? fetchError.message : 'Unknown error'
        );
      } catch (trackingError) {
        console.warn('⚠️ Usage tracking failed:', trackingError);
      }
      
      if (fetchError.name === 'AbortError') {
        const timeoutSeconds = Math.round(elapsed / 1000);
        const maxAllowed = scanType === 'meal' ? 300 : 240; // 5 minutes for meals, 4 minutes for inventory (increased from 3 minutes)
        
        if (elapsed >= (scanType === 'meal' ? 300000 : 240000)) { // Updated timeout check for 4 minutes inventory
          throw new Error(`${scanType === 'meal' ? 'Meal' : 'Image'} recognition timed out after ${timeoutSeconds} seconds (${maxAllowed}s maximum for ${scanType} scans). The image may be too large or your connection is very slow. Please try again with a smaller image.`);
        } else {
          throw new Error(`${scanType === 'meal' ? 'Meal' : 'Image'} recognition timed out after ${timeoutSeconds} seconds during '${currentPhase}' phase. Please check your internet connection and try again.`);
        }
      }
      
      throw fetchError;
    }
    
  } catch (error: any) {
    console.error('💥 Image recognition API error:', error);
    console.error('💥 Error type:', typeof error);
    console.error('💥 Error stack:', error.stack);
    
    // Enhanced error handling with context
    if (error.message?.includes('timeout')) {
      throw new Error(`${error.message} Network conditions may be affecting the request.`);
    }
    
    if (error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
      throw new Error('Network connection error. Please check your internet connection and try again.');
    }
    
    if (error.message?.includes('authentication') || error.message?.includes('Unauthorized')) {
      throw new Error('Authentication failed. Please log out and log back in.');
    }
    
    throw error;
  }
}

export async function generateImage(prompt: string) {
  if (isOfflineMode()) {
    throw new Error('Image generation is not available in offline mode');
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    const fetchPromise = fetch(`${SUPABASE_URL}/functions/v1/image-generation`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ prompt }),
      signal: AbortSignal.timeout(60000), // 60 second timeout for image generation
    });

    const response = await withTimeout(fetchPromise, 60000);

    if (!response.ok) {
      throw new Error(`Failed to generate image: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error generating image:', error);
    
    // Handle specific error types
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      throw new Error('Image generation request timed out. Please try again.');
    }
    
    if (error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
      throw new Error('Network connection error. Please check your internet connection.');
    }
    
    throw error;
  }
}

/**
 * Uploads an image file to Supabase Storage and returns the storage path (not public URL).
 * @param file The image file to upload
 * @param userId The user's Supabase auth.uid (required)
 */
export async function uploadImageToStorage(file: File, userId: string): Promise<string> {
  if (isOfflineMode()) {
    throw new Error('Image upload is not available in offline mode');
  }
  if (!userId) {
    throw new Error('User ID is required for secure image upload');
  }
  // Generate a unique filename
  const timestamp = Date.now();
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${userId}/${timestamp}-${Math.random().toString(36).slice(2)}.${ext}`;

  // Upload to 'images' bucket
  const { error } = await supabase.storage.from('images').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });

  if (error) {
    throw new Error('Failed to upload image: ' + error.message);
  }

  return path;
}

/**
 * Generates a signed URL for a Supabase Storage image
 * @param path The storage path within the bucket
 * @param expiresIn Seconds until the URL expires (default: 60s)
 */
export async function getSignedImageUrl(path: string, expiresIn: number = 60): Promise<string> {
  // Validate input path
  if (!path || path.trim() === '') {
    throw new Error('Invalid key: Empty or undefined path provided to getSignedImageUrl');
  }
  
  // Determine which bucket to use based on path characteristics
  let bucket = 'images'; // Default bucket for meal photos
  
  // If path contains recipe-related indicators, use recipe-images bucket
  if (path.includes('recipe-images') || path.includes('recipe')) {
    bucket = 'recipe-images';
    // Remove recipe-images prefix if it exists in path
    path = path.replace(/^recipe-images\//, '');
  }
  
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
  if (error || !data?.signedUrl) {
    throw new Error(`Failed to generate signed URL for image in ${bucket} bucket: ${error?.message || 'Unknown error'}`);
  }
  return data.signedUrl;
}

// Simple cache for signed URLs to prevent duplicates
const signedUrlCache = new Map<string, { url: string; timestamp: number; expiresIn: number }>();

/**
 * Generates a signed URL for an image if it's a storage path, or returns the URL if it's already a signed URL
 * @param imagePathOrUrl Storage path or existing signed URL
 * @param expiresIn Seconds until the URL expires (default: 86400s = 24 hours for saved meals)
 */
export async function getImageDisplayUrl(imagePathOrUrl: string, expiresIn: number = 86400): Promise<string> {
  // Validate input - return fallback for empty or invalid paths
  if (!imagePathOrUrl || imagePathOrUrl.trim() === '') {
    console.warn('⚠️ Empty or invalid image path provided to getImageDisplayUrl');
    return '/food.jpg';
  }
  
  // If it's already a full HTTP/HTTPS URL (includes protocol), return as is
  if (imagePathOrUrl.startsWith('http://') || imagePathOrUrl.startsWith('https://')) {
    return imagePathOrUrl;
  }
  
  // If it's already a signed URL (contains 'supabase' and 'token='), return as is
  if (imagePathOrUrl.includes('supabase') && imagePathOrUrl.includes('token=')) {
    return imagePathOrUrl;
  }
  
  // Check cache first (only use cached URL if it's still valid for at least 30 minutes)
  const cached = signedUrlCache.get(imagePathOrUrl);
  if (cached) {
    const age = Date.now() - cached.timestamp;
    const remainingLife = cached.expiresIn * 1000 - age;
    const minRemainingTime = 30 * 60 * 1000; // 30 minutes
    
    if (remainingLife > minRemainingTime) {
      return cached.url;
    } else {
      // Remove expired cache entry
      signedUrlCache.delete(imagePathOrUrl);
    }
  }
  
  // If it's a storage path, try to generate a fresh signed URL
  try {
    // For older meal images that might be in 'images' bucket
    const signedUrl = await getSignedImageUrl(imagePathOrUrl, expiresIn);
    
    // Cache the result
    signedUrlCache.set(imagePathOrUrl, {
      url: signedUrl,
      timestamp: Date.now(),
      expiresIn: expiresIn
    });
    
    return signedUrl;
  } catch (error) {
    console.warn('⚠️ Failed to generate signed URL for path:', imagePathOrUrl.substring(0, 30) + '...', error);
    
    // For meal tracker images, use fallback instead of returning broken path
    return '/food.jpg'; // Fallback to default food image
  }
}

/**
 * Deletes an image from Supabase Storage
 * @param imagePath The storage path to delete (e.g. userId/filename.jpg)
 */
export async function deleteImageFromStorage(imagePath: string): Promise<void> {
  try {
    const { error } = await supabase.storage.from('images').remove([imagePath]);
    if (error) {
      throw new Error('Failed to delete image: ' + error.message);
    }
    console.log('🗑️ Successfully deleted image from storage:', imagePath);
  } catch (error: any) {
    console.error('❌ Failed to delete image from storage:', error);
    throw error;
  }
}

/**
 * Cleans up orphaned images that are older than the specified age and not referenced in the database
 * WARNING: This should NOT be called automatically on page load to avoid interfering with active scans
 * Only call this manually for periodic maintenance (e.g., once per day)
 * @param userId The user ID to clean up images for
 * @param maxAgeHours Maximum age of images to keep (default: 24 hours)
 */
export async function cleanupOrphanedImages(userId: string, maxAgeHours: number = 24): Promise<void> {
  try {
    console.log(`🧹 Manual cleanup requested: orphaned images for user ${userId} (older than ${maxAgeHours}h)`);
    console.log('⚠️ This cleanup should only run during manual maintenance, not on page load');
    
    // List all images for this user
    const { data: files, error: listError } = await supabase.storage
      .from('images')
      .list(userId, {
        limit: 1000,
        sortBy: { column: 'created_at', order: 'desc' }
      });

    if (listError) {
      throw new Error('Failed to list images: ' + listError.message);
    }

    if (!files || files.length === 0) {
      console.log('📭 No images found for user');
      return;
    }

    console.log(`📂 Found ${files.length} images for user`);

    // Get all meal image paths from database to exclude them from cleanup
    const { data: meals, error: mealsError } = await supabase
      .from('user_meals')
      .select('image_url')
      .eq('user_id', userId);

    if (mealsError) {
      console.warn('⚠️ Failed to fetch saved meals, skipping database check:', mealsError);
    }

    const savedImagePaths = new Set(meals?.map(meal => meal.image_url.split('/').pop()) || []);
    console.log(`💾 Found ${savedImagePaths.size} saved meal images to preserve`);

    // Find orphaned images older than maxAgeHours
    const cutoffTime = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000);
    const orphanedFiles = files.filter(file => {
      // Skip if this image is referenced in a saved meal
      if (savedImagePaths.has(file.name)) {
        console.log(`🛡️ Preserving saved meal image: ${file.name}`);
        return false;
      }

      // Check if file is older than cutoff
      const fileDate = new Date(file.created_at || file.updated_at);
      const isOld = fileDate < cutoffTime;
      
      if (isOld) {
        console.log(`🗑️ Marking for deletion: ${file.name} (created: ${fileDate.toISOString()})`);
      }
      
      return isOld;
    });

    if (orphanedFiles.length === 0) {
      console.log('✨ No orphaned images found to clean up');
      return;
    }

    console.log(`🧹 Cleaning up ${orphanedFiles.length} orphaned images`);

    // Delete orphaned files
    const pathsToDelete = orphanedFiles.map(file => `${userId}/${file.name}`);
    const { error: deleteError } = await supabase.storage
      .from('images')
      .remove(pathsToDelete);

    if (deleteError) {
      throw new Error('Failed to delete orphaned images: ' + deleteError.message);
    }

    console.log(`✅ Successfully cleaned up ${orphanedFiles.length} orphaned images`);
  } catch (error: any) {
    console.error('❌ Failed to cleanup orphaned images:', error);
    // Don't throw - cleanup errors shouldn't break the app
  }
}

/**
 * Performs daily maintenance cleanup if it hasn't been done today
 * This is safer than automatic cleanup on every page load
 * Call this once per app session or on app startup
 */
export async function performDailyMaintenanceIfNeeded(): Promise<void> {
  try {
    const lastCleanup = localStorage.getItem('lastCleanupDate');
    const today = new Date().toDateString();
    
    if (lastCleanup === today) {
      console.log('ℹ️ Daily cleanup already performed today, skipping');
      return;
    }
    
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.id) {
      console.log('🗓️ Performing daily maintenance cleanup');
      await cleanupOrphanedImages(session.user.id, 48); // Use 48 hours for daily cleanup to be extra safe
      localStorage.setItem('lastCleanupDate', today);
      console.log('✅ Daily maintenance completed');
    }
  } catch (error: any) {
    console.error('❌ Daily maintenance failed:', error);
    // Don't throw - maintenance errors shouldn't break the app
  }
}

/**
 * Cancels a meal scan by cleaning up the uploaded image and clearing session data
 * Call this when users cancel or close the nutrition analysis without saving
 * This is the ONLY automatic cleanup that should happen - triggered by explicit user action
 * @param imagePath The storage path of the image to clean up
 */
export async function cancelMealScan(imagePath?: string): Promise<void> {
  try {
    console.log('🚫 User canceled meal scan - cleaning up unsaved data');
    
    // Clear session storage
    sessionStorage.removeItem('mealAnalysisData');
    console.log('🗑️ Cleared meal analysis data from session storage');

    // Delete the uploaded image if path is provided
    if (imagePath) {
      console.log('🗑️ Deleting unsaved meal image:', imagePath);
      await deleteImageFromStorage(imagePath);
      console.log('✅ Successfully deleted unsaved meal image from storage');
    } else {
      console.log('ℹ️ No image path provided - only clearing session data');
    }
  } catch (error: any) {
    console.error('❌ Failed to cleanup canceled meal scan:', error);
    // Don't throw - cleanup errors shouldn't break the app
  }
}
